import { useEffect, useRef, useState } from "react";
import { Marker } from "react-leaflet";
import { DivIcon, LatLng, LeafletMouseEvent } from "leaflet";
import { Coordinates } from "../../types/MapData";
import { NameID } from "common/types/NameID";
import { useCylinderChart } from "../hooks/useCylinderChart";
import { OtherCountControl } from "./OtherCountControl";
import { PinData, PinDrawer } from "./PinDrawer";
import { getPalette } from "../Palette";

export type ChartBar = {
  label: NameID;
  count: number;
};

export type ChartOverlayPin = {
  categoryId: number;
  data: PinData;
};

export type ChartOverlayData = {
  area: NameID;
  center: Coordinates;
  bars: ChartBar[];
  pin?: ChartOverlayPin[];
};

type Props = {
  chartData: ChartOverlayData[];
  showCharts: boolean;
  showPins: boolean;
  onBarSelected?: (areaId: number, pin?: Coordinates) => void;
  onBarDeselected?: () => void;
};

// チャート表示のベース
export const ChartOverlayDrawer = ({
  chartData,
  showCharts,
  showPins,
  onBarSelected,
  onBarDeselected,
}: Props) => {
  const { createCylinderChartSvg } = useCylinderChart();

  const otherOverlay = chartData.filter((o) => o.area.id < 0);
  const regularOverlays = chartData.filter((o) => o.area.id >= 0);
  const globalMax = Math.max(
    ...regularOverlays.map((o) => o.bars.reduce((s, b) => s + b.count, 0)),
    1,
  );
  const [selected, setSelected] = useState<{ areaId: number; pin?: Coordinates } | null>(null);
  const pinWithColor = (pinData: PinData, categoryId: number): PinData => {
    return { ...pinData, color: getPalette(categoryId).front };
  };
  const pins = chartData.flatMap((overlay) => {
    const visibleCategoryIds = new Set(overlay.bars.map((bar) => bar.label.id));
    return (
      overlay.pin
        ?.filter((pin) => visibleCategoryIds.has(pin.categoryId))
        .map((pin) => pinWithColor(pin.data, pin.categoryId)) ?? []
    );
  });

  const isSamePoint = (a?: Coordinates, b?: Coordinates) => {
    if (!a || !b) return false;
    return a.x === b.x && a.y === b.y;
  };
  const selectedAreaOverlay = selected
    ? chartData.find((x) => x.area.id === selected.areaId)
    : undefined;
  const selectedAreaPins =
    selectedAreaOverlay?.pin
      ?.filter((p) => selectedAreaOverlay.bars.some((bar) => bar.label.id === p.categoryId))
      .map((p) => pinWithColor(p.data, p.categoryId)) ?? [];
  const selectedPin = selected?.pin
    ? pins.find((pin) => isSamePoint(pin.point, selected.pin))
    : undefined;

  const barClicked = (overlay: ChartOverlayData) => {
    if (selected?.areaId === overlay.area.id) {
      setSelected(null);
      onBarDeselected?.();
    } else {
      setSelected({ areaId: overlay.area.id });
      onBarSelected?.(overlay.area.id);
    }
  };

  const otherClicked = (areaId: number) => {
    if (selected?.areaId === areaId) {
      setSelected(null);
      onBarDeselected?.();
      return;
    }

    if (areaId === -2) {
      setSelected({ areaId });
      onBarSelected?.(areaId);
      return;
    }

    setSelected({ areaId });
    onBarSelected?.(areaId);
  };

  const pinClicked = (pin: PinData) => {
    const overlay = chartData.find((x) =>
      x.pin?.some((p) => p.data.point.x === pin.point.x && p.data.point.y === pin.point.y),
    );
    if (!overlay) return;

    const isSameSelection =
      selected?.areaId === overlay.area.id && isSamePoint(selected.pin, pin.point);
    if (isSameSelection) {
      setSelected(null);
      onBarDeselected?.();
    } else {
      setSelected({ areaId: overlay.area.id, pin: pin.point });
      onBarSelected?.(overlay.area.id, pin.point);
    }
  };

  const prevChartDataRef = useRef(chartData);

  useEffect(() => {
    if (prevChartDataRef.current === chartData) return;
    prevChartDataRef.current = chartData;
    if (!selected) return;

    setSelected(null);
    onBarDeselected?.();
  }, [chartData, onBarDeselected, selected]);

  return (
    <>
      {/* その他部分 */}
      {chartData.length > 0 && (
        <>
          <OtherCountControl
            data={otherOverlay}
            selectedAreaId={selected?.areaId}
            onAreaClicked={otherClicked}
          />
        </>
      )}
      {/* グラフ表示部分 */}
      {showCharts &&
        regularOverlays.map((overlay) => {
          const isSelected = selected?.areaId === overlay.area.id && !selected.pin;
          const { svg, iconW, iconH, anchorX, anchorY } = createCylinderChartSvg(
            overlay.bars,
            overlay.area.name,
            globalMax,
            isSelected,
          );
          const icon = new DivIcon({
            html: svg,
            className: "",
            iconSize: [iconW, iconH],
            iconAnchor: [anchorX, anchorY], // ベースラインをポリゴン中心に合わせる
          });
          return (
            <Marker
              key={overlay.area.id}
              position={new LatLng(overlay.center.y, overlay.center.x)}
              icon={icon}
              interactive={true}
              zIndexOffset={500}
              eventHandlers={{
                click: (e: LeafletMouseEvent) => {
                  e.originalEvent.stopPropagation();
                  barClicked(overlay);
                },
              }}
            />
          );
        })}
      {showPins && (
        <PinDrawer
          pins={pins}
          selectedPin={selectedPin}
          selectedPins={selected?.pin ? undefined : selectedAreaPins}
          onClicked={pinClicked}
        />
      )}
    </>
  );
};
